import clsx from "clsx";
import React from "react";
import useQRCodeGenerator from "react-hook-qrcode-svg";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Theme, useTheme } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";

import * as Utils from "./GenerateQRCodeDialog.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        position: "relative",
        gap: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
    },
    advancedModeQrPosition: {
        margin: "0 auto",
        alignItems: "center",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
    },
    fullscreenButtonContainer: {
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
    },
    fullscreenContainer: {
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.emteria.background.paper,
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
}));

type QrCodeProps = {
    value: string;
    advancedMode: boolean;
};

export const QrCode = (props: QrCodeProps) => {
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const theme = useTheme();
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    const { path, viewBox } = useQRCodeGenerator(props.value, Utils.qrCodeLevel, Utils.qrCodeBorder);

    const onToggleFullscreen = async () => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await container.requestFullscreen();
        }
    };

    React.useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };

        document.addEventListener("fullscreenchange", onFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", onFullscreenChange);
        };
    }, []);

    if (isFullscreen) {
        return (
            <Box
                ref={containerRef}
                className={clsx(classes.fullscreenContainer, props.advancedMode && classes.advancedModeQrPosition)}
            >
                <Box className={classes.fullscreenButtonContainer}>
                    <SecondaryButton onClick={onToggleFullscreen}>{t("modals:generateQrCode.minimizeQrCode")}</SecondaryButton>
                </Box>

                <svg width="100%" height="100%" viewBox={viewBox} preserveAspectRatio="xMidYMid meet" stroke="none">
                    <rect width="100%" height="100%" fill={theme.palette.emteria.text.inverse} />
                    <path d={path} fill={theme.palette.emteria.text.primary} />
                </svg>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} className={clsx(classes.container, props.advancedMode && classes.advancedModeQrPosition)}>
            <svg
                width={Utils.qrCodeSize}
                height={Utils.qrCodeSize}
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                stroke="none"
            >
                <rect width="100%" height="100%" fill={theme.palette.emteria.text.inverse} />
                <path d={path} fill={theme.palette.emteria.text.primary} />
            </svg>

            <SecondaryButton onClick={onToggleFullscreen}>{t("modals:generateQrCode.maximizeQrCode")}</SecondaryButton>
        </Box>
    );
};
